import { useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Loader2, CalendarSearch, XCircle, CalendarClock } from "lucide-react";

const inputCls =
  "px-4 py-3 rounded-xl border border-[#E2E8F0] bg-white text-[#0B1B2B] text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-colors";

export const ManageBooking = () => {
  const [form, setForm] = useState({ confirmation_id: "", email: "" });
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resched, setResched] = useState(null);
  const [slots, setSlots] = useState([]);
  const [done, setDone] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setDone("");
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/manage/lookup", form);
      setBooking(data);
    } catch (err) {
      setBooking(null);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const act = async (action, date, slot) => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/schedule/manage", { ...form, action, date, slot });
      setDone(action === "cancel" ? "Your consultation has been cancelled. A confirmation email is on its way."
        : `Rescheduled to ${data.date} at ${data.slot} ET. A confirmation email is on its way.`);
      setBooking(null);
      setResched(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadSlots = async (date) => {
    setResched({ date, slot: "" });
    if (!date) return setSlots([]);
    const { data } = await api.get("/schedule/availability", { params: { date } });
    setSlots(data.slots.filter((s) => s.available).map((s) => s.time));
  };

  return (
    <div className="mt-8 max-w-xl bg-white rounded-xl shadow-2xl p-7" data-testid="manage-booking-panel">
      <h3 className="font-serif text-xl text-[#0B1B2B] font-semibold flex items-center gap-2">
        <CalendarSearch size={19} className="text-[#D4AF37]" /> Manage an Existing Booking
      </h3>
      {done ? (
        <div className="mt-5">
          <p className="text-sm text-emerald-700 font-medium" data-testid="manage-booking-done">{done}</p>
          <button onClick={() => { setDone(""); setForm({ confirmation_id: "", email: "" }); }} className="mt-3 text-xs font-semibold text-[#0B1B2B] gold-underline" data-testid="manage-another-button">
            Manage another booking
          </button>
        </div>
      ) : (
        <>
          <form onSubmit={lookup} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Confirmation ID (AR-CONSULT-…)" required value={form.confirmation_id} onChange={(e) => setForm({ ...form, confirmation_id: e.target.value })} data-testid="manage-confirmation-input" />
            <input className={inputCls} type="email" placeholder="Email used to book" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="manage-email-input" />
            <button type="submit" disabled={busy} className="md:col-span-2 bg-[#0B1B2B] text-white text-sm font-semibold py-3 rounded-xl hover:bg-[#152a40] transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-testid="manage-lookup-button">
              {busy && <Loader2 size={15} className="animate-spin" />} Find My Booking
            </button>
          </form>
          {error && <p className="mt-3 text-sm text-red-600" data-testid="manage-error">{error}</p>}
          {booking && (
            <div className="mt-5 border-t border-[#E2E8F0] pt-5" data-testid="manage-booking-details">
              <div className="text-sm text-[#0B1B2B] font-medium">
                {booking.date} at {booking.slot} ET · {booking.name}
                <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded ${booking.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>{booking.status}</span>
              </div>
              {booking.status === "confirmed" && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <button onClick={() => act("cancel")} disabled={busy} className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors" data-testid="manage-cancel-button">
                    <XCircle size={14} /> Cancel Booking
                  </button>
                  <button onClick={() => setResched(resched ? null : { date: "", slot: "" })} className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#0B1B2B] hover:border-[#D4AF37] transition-colors" data-testid="manage-reschedule-toggle">
                    <CalendarClock size={14} className="text-[#D4AF37]" /> Reschedule
                  </button>
                </div>
              )}
              {resched && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <input type="date" className={inputCls} value={resched.date} onChange={(e) => loadSlots(e.target.value)} data-testid="manage-resched-date" />
                  <select className={inputCls} value={resched.slot} onChange={(e) => setResched({ ...resched, slot: e.target.value })} data-testid="manage-resched-slot">
                    <option value="">Select slot…</option>
                    {slots.map((t) => <option key={t} value={t}>{t} ET</option>)}
                  </select>
                  <button onClick={() => act("reschedule", resched.date, resched.slot)} disabled={busy || !resched.date || !resched.slot} className="bg-[#D4AF37] text-[#0B1B2B] text-xs font-semibold px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="manage-resched-confirm">
                    Confirm New Time
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
